'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { MapPin, Calendar, Database, Target, Zap, Info } from 'lucide-react';
import { EsimPlan, listCountries, loadPlansAsync, getCountryPlans } from '@/lib/pricingData';
import { ROAMING_BENCHMARKS, CarrierType, getBenchmarkForCarrier } from '../lib/roamingBenchmarks';
import { useTranslations } from '@/lib/i18n-client';
import AppSelect from '@/components/ui/AppSelect';

interface CalculatorFormProps {
  onCalculate: (results: CalculationResult) => void;
  initialDestination?: string;
}

export interface CalculationResult {
  destination: { code: string; name: string };
  days: number;
  usageProfile: 'light' | 'normal' | 'heavy';
  carrierCost: number;
  supraCost: number;
  savings: number;
  bestPlan: EsimPlan | null;
}

const USAGE_MULTIPLIER = {
  light: 0.5, // 500MB
  normal: 1,  // 1GB
  heavy: 2    // 2 GB
};

export default function CalculatorForm({ onCalculate, initialDestination }: CalculatorFormProps) {
  const { t } = useTranslations();
  const [plans, setPlans] = useState<EsimPlan[]>([]);
  const [countries, setCountries] = useState<{ code: string; name: string }[]>([]);
  const [search, setSearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<{ code: string; name: string } | null>(null);
  const [days, setDays] = useState(7);
  const [usage, setUsage] = useState<'light' | 'normal' | 'heavy'>('normal');
  const [carrierType, setCarrierType] = useState<CarrierType>('US');

  useEffect(() => {
    loadPlansAsync().then(p => {
      setPlans(p);
      const countryList = listCountries(p);
      setCountries(countryList);

      // Auto-select if initialDestination is provided
      if (initialDestination) {
        const match = countryList.find(c => 
          c.name.toLowerCase() === initialDestination.toLowerCase() ||
          c.code.toLowerCase() === initialDestination.toLowerCase()
        );
        if (match) {
          setSelectedCountry(match);
        }
      }
    });
  }, [initialDestination]);

  const filteredCountries = useMemo(() => {
    if (!search) return [];
    return countries.filter(c => 
      c.name.toLowerCase().includes(search.toLowerCase()) || 
      c.code.toLowerCase().includes(search.toLowerCase())
    ).slice(0, 5);
  }, [countries, search]);

  const handleCalculate = () => {
    if (!selectedCountry) return;

    const benchmark = getBenchmarkForCarrier(carrierType);
    const carrierTotal = benchmark.dailyFeeUSD * days;
    
    const countryPlans = getCountryPlans(plans, selectedCountry.code);
    const requiredGBs = days * USAGE_MULTIPLIER[usage];
    
    // Find best plan: cheapest plan that covers the GB requirement and validity
    const bestPlan = countryPlans.find(p => p.GBs >= requiredGBs && p.ValidityDays >= days) 
                  || countryPlans[countryPlans.length - 1] // Fallback to largest
                  || null;

    const supraTotal = bestPlan ? bestPlan.PriceUSD : 0;

    onCalculate({
      destination: selectedCountry,
      days,
      usageProfile: usage,
      carrierCost: carrierTotal,
      supraCost: supraTotal,
      savings: Math.max(0, carrierTotal - supraTotal),
      bestPlan
    });
  };

  return (
    <div className="glass p-8 rounded-[2.5rem] border border-border/50 shadow-2xl space-y-8">
      {/* Step 1: Destination */}
      <div className="space-y-4">
        <label className="text-xs font-black uppercase tracking-[0.2em] text-primary flex items-center gap-2">
          <MapPin className="w-3 h-3" /> {t('calculator.step1Label', 'Step 1: Destination')}
        </label>
        <div className="relative">
          <input
            type="text"
            placeholder={t('calculator.destinationPlaceholder', 'Search country (e.g. Japan, France...)')}
            value={selectedCountry ? selectedCountry.name : search}
            onChange={(e) => {
              setSearch(e.target.value);
              if (selectedCountry) setSelectedCountry(null);
            }}
            className="w-full bg-background/50 border border-border/30 rounded-2xl px-6 py-4 text-lg font-medium focus:ring-2 focus:ring-primary/20 transition-all"
          />
          {filteredCountries.length > 0 && !selectedCountry && (
            <div className="absolute top-full left-0 right-0 mt-2 glass rounded-2xl border border-border/50 shadow-xl overflow-hidden z-10 animate-in fade-in slide-in-from-top-2">
              {filteredCountries.map(c => (
                <button
                  key={c.code}
                  onClick={() => {
                    setSelectedCountry(c);
                    setSearch('');
                  }}
                  className="w-full px-6 py-4 text-left hover:bg-primary/10 transition-colors flex items-center justify-between group"
                >
                  <span className="font-bold">{c.name}</span>
                  <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Step 2: Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <label className="text-xs font-black uppercase tracking-[0.2em] text-primary flex items-center gap-2">
            <Calendar className="w-3 h-3" /> {t('calculator.step2Label', 'Step 2: Trip Duration')}
          </label>
          <div className="flex flex-col gap-4">
             <div className="flex justify-between text-sm font-bold opacity-70">
                <span>{t('calculator.day1', '1 Day')}</span>
                <span className="text-primary">{days} {t('calculator.days', 'Days')}</span>
                <span>{t('calculator.days30', '30+ Days')}</span>
             </div>
             <input
              type="range"
              min="1"
              max="30"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="w-full accent-primary h-2 bg-border/30 rounded-lg cursor-pointer"
            />
          </div>
        </div>

        <div className="space-y-4">
          <label className="text-xs font-black uppercase tracking-[0.2em] text-primary flex items-center gap-2">
            <Database className="w-3 h-3" /> {t('calculator.step3Label', 'Step 3: Daily Data Usage')}
          </label>
          <AppSelect
            value={usage}
            ariaLabel={t('calculator.step3Label', 'Step 3: Daily Data Usage')}
            onValueChange={(value) => setUsage(value as any)}
            options={[
              { value: 'light', label: t('calculator.usageLight', 'Light (500MB/day)') },
              { value: 'normal', label: t('calculator.usageNormal', 'Normal (1GB/day)') },
              { value: 'heavy', label: t('calculator.usageHeavy', 'Heavy (2GB+/day)') },
            ]}
          />
        </div>
      </div>

      {/* Step 4: Carrier */}
      <div className="space-y-4">
        <label className="text-xs font-black uppercase tracking-[0.2em] text-primary flex items-center gap-2">
          <Target className="w-3 h-3" /> {t('calculator.step4Label', 'Step 4: Your Home Carrier')}
        </label>
        <div className="flex flex-wrap gap-3">
          {ROAMING_BENCHMARKS.map(c => (
            <button
              key={c.type}
              onClick={() => setCarrierType(c.type)}
              className={`px-6 py-3 rounded-xl border text-sm font-bold transition-all ${
                carrierType === c.type 
                ? 'bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/20' 
                : 'bg-background hover:border-primary/50 border-border/50'
              }`}
            >
              {c.type === 'US' ? t('calculator.carrierUS', 'US (AT&T/Verizon)') : c.type === 'UK' ? t('calculator.carrierUK', 'UK (Vodafone/O2)') : c.type === 'EUROPE' ? t('calculator.carrierEU', 'EU Member') : t('calculator.carrierOther', 'Other')}
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={handleCalculate}
        disabled={!selectedCountry}
        className="w-full bg-primary text-primary-foreground py-6 rounded-2xl font-black text-xl hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:hover:scale-100"
      >
        <Zap className="w-6 h-6 fill-current" />
        {t('calculator.calculateCta', 'Calculate My Savings')}
      </button>

      <div className="pt-4 flex items-center gap-3 text-xs text-muted-foreground leading-relaxed">
         <Info className="w-4 h-4 shrink-0" />
         <p>{t('calculator.benchmarkNote', 'Benchmarks are based on standard carrier daily roaming passes as of April 2026. Actual carrier costs may vary based on your specific plan.')}</p>
      </div>
    </div>
  );
}

function ChevronRight({ className }: { className?: string }) {
  return (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m9 18 6-6-6-6"/>
    </svg>
  );
}
